import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { getErrorLoadingIndividualsByEntity, getIndividualsByEntity, getIndividualsIsLoadingByEntity, populateIndividualsByEntity } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  useMaterialReactTable,
} from "material-react-table";
import * as React from "react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";

const NameCellContainer = styled(Box)(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type EntityMembersTableInputProps = {
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<Individual> }) => React.ReactNode;
};

export default function EntityMembersTable(props: Readonly<EntityMembersTableInputProps>) {
  const { renderTopToolbarCustomActions } = props;
  const [query, setQuery] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<MRT_SortingState>([]);
  const [pagination, setPagination] = React.useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();

  const individuals = useSelector(getIndividualsByEntity(session.user!.legalEntityId));
  const isLoading = useSelector(getIndividualsIsLoadingByEntity(session.user!.legalEntityId));
  const hasError = useSelector(getErrorLoadingIndividualsByEntity(session.user!.legalEntityId));

  if (!individuals && !isLoading && !hasError) {
    dispatch(populateIndividualsByEntity({ session, legalEntityId: session.user!.legalEntityId }));
  }

  function handleViewProfile(individualId?: Guid): void {
    if (!individualId) return;

    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

    openDialog(
      {
        titleStyle: {
          paddingBottom: 0
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: <ViewIndividualProfile individualId={individualId} />,
        onClose: () => {
          navigate(parentPath.pathname)
        }
      }
    );
  }

  function renderNameColumn(individual: Individual): React.ReactNode {
    if (!individual) return <></>;

    let name = `${individual.profile.firstName} ${individual.profile.lastName}`;
    let email = individual.profile.email?.value;

    const contactLink = <RowTitle>{name}</RowTitle>;

    return (
      <NameCellContainer onClick={() => handleViewProfile(individual.id)}>
        <IndividualAvatar 
          individual={individual} 
          session={session} 
        />
        <NameText primary={contactLink} secondary={email} />
      </NameCellContainer>
    );
  }

  function renderRoleColumn(_: Individual) {
    console.warn("Role rendering is simplified");
    // const entityMember: EntityMember = data;
    // if (entityMember.isOfficer) return 'Officer';
    // if (entityMember.isAdmin) return 'Admin';
    return "Member";
  }

  function renderPermissionsColumn(data: Individual) {
    return (
      <Link
        component={RouterLink}
        to={{
          pathname: "/administration/permissions/",
          search: `?granteeUserId=${data.userId}`,
        }}
      >
        View Permissions
      </Link>
    );
  }

  const membersColumnHelper = createMRTColumnHelper<Individual>();

  let columns = useMemo(() => [
    membersColumnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameColumn(row.original)
    }),
    membersColumnHelper.display({
      header: "Role",
      id: "isAdmin",
      Cell: ({ row }) => renderRoleColumn(row.original)
    }),
    membersColumnHelper.display({
      header: "Permissions",
      id: "permissions",
      Cell: ({ row }) => renderPermissionsColumn(row.original)
    })
  ], [individuals]);

  const table = useMaterialReactTable({
    columns,
    data: individuals ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: hasError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: individuals?.length ?? 0,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: !!hasError,
      showProgressBars: isLoading,
      sorting,
    },
  });

  return <MaterialReactTable table={table} />;
}
